'use client';

import { useAppInsights } from '../../components/AppInsightsWrapper/AppInsightsWrapper';

import {
	type AccountInfo,
	AzureCloudInstance,
	BrowserCacheLocation,
	type Configuration,
	type ILoggerCallback,
	InteractionType,
	Logger,
	LogLevel,
	type RedirectRequest,
	type SilentRequest,
} from '@azure/msal-browser';
import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { secondsToMilliseconds } from 'date-fns';
import { useMemo } from 'react';

import { useCurrentURL } from '../../app/(client)/(authenticated)/jobs/(jobId)/jobLink/useCurrentURL';
import { logDebug, logError, logInfo, logWarning } from './logging/writeLog';

// Map different trace levels to app insight logging functions
const traceLevels: Record<
	number,
	(appInsights: ReactPlugin, text: string, properties?: Record<string, unknown>) => void
> = {};
traceLevels[LogLevel.Error] = logError;
traceLevels[LogLevel.Warning] = logWarning;
traceLevels[LogLevel.Info] = logInfo;
traceLevels[LogLevel.Verbose] = logDebug;

const tenantId = process.env['NEXT_PUBLIC_AZURE_TENANT_ID'];
if (!tenantId) {
	console.error('Missing Azure Tenant ID');
}
const clientId = process.env['NEXT_PUBLIC_AZURE_CLIENT_ID'];
if (!clientId) {
	console.error('Missing Azure Client ID');
}
const redirectUri = process.env['NEXT_PUBLIC_AZURE_REDIRECT_URI'];
if (!redirectUri) {
	console.error('Missing Azure Redirect URI');
}

// Config object to be passed to Msal on creation
export const msalConfig: Configuration = {
	auth: {
		azureCloudOptions: {
			azureCloudInstance: AzureCloudInstance.AzurePublic,
			tenant: tenantId,
		},
		clientId: clientId ?? '',
		redirectUri: redirectUri,
	},
	cache: {
		cacheLocation: BrowserCacheLocation.SessionStorage,
	},
	system: {
		loggerOptions: {
			logLevel: LogLevel.Error,
		},
		// Increase timeouts (SDK default is 10s)
		iframeBridgeTimeout: secondsToMilliseconds(20),
		nativeBrokerHandshakeTimeout: secondsToMilliseconds(20),
		popupBridgeTimeout: secondsToMilliseconds(20),
		redirectNavigationTimeout: secondsToMilliseconds(20),
	},
	telemetry: {
		application: {
			appName: 'OTW Admin UI',
			appVersion: process.env['NEXT_PUBLIC_CLIENT_BUILD_DATE'] ?? 'unknown',
		},
	},
};

export function useMsalLogger(): Logger {
	const appInsights = useAppInsights();

	const msalLogger: Logger = useMemo(() => {
		const loggingCallback: ILoggerCallback = (level: LogLevel, message: string, containsPii: boolean) => {
			if (containsPii) {
				return;
			}
			let logger = traceLevels[level];
			if (!logger) {
				logger = console.log;
			}
			logger(appInsights, message);
		};
		const logger = new Logger({
			loggerCallback: loggingCallback,
			piiLoggingEnabled: false,
			logLevel: LogLevel.Error,
		});
		return logger;
	}, [appInsights]);

	return msalLogger;
}

export function useRedirectRequest(): RedirectRequest {
	const url = useCurrentURL();

	const request = useMemo(() => {
		const newRequest: RedirectRequest = {
			scopes: ['openid'],
			domainHint: 'dteenergy.com',

			// Come back to where we are
			redirectStartPage: url,
		};
		return newRequest;
	}, [url]);

	return request;
}

// Which type of login we want to use as a fallback
export function getLoginMethod(): InteractionType {
	return InteractionType.Redirect;
}

/**
 * Defaults for getting a silent token request
 *
 * @param account
 * @param forceRefresh
 * @returns
 */
export function getSilentRequest(account: AccountInfo | null, forceRefresh?: boolean): SilentRequest {
	const tokenRequest: SilentRequest = {
		scopes: ['openid'],
		extraQueryParameters: { domain_hint: 'dteenergy.com' },
		forceRefresh: forceRefresh,
	};
	if (account) {
		tokenRequest.account = account;
	}

	return tokenRequest;
}
